export type TrafficRange = '1h' | '6h' | '24h' | '7d';

export const RANGE_MS: Record<TrafficRange, number> = {
  '1h': 3600_000,
  '6h': 6 * 3600_000,
  '24h': 24 * 3600_000,
  '7d': 7 * 24 * 3600_000,
};

export const RANGES: TrafficRange[] = ['1h', '6h', '24h', '7d'];

/**
 * How often a surface's window advances, by how long that window is.
 *
 * A 7-day chart re-fetching every minute spends a megabyte to move its right
 * edge by a pixel.
 */
export function refreshMs(range: TrafficRange): number {
  return range === '1h' || range === '6h' ? 60_000 : 300_000;
}

/**
 * What the Views page offers, which is the panel presets plus a live state.
 *
 * `Live` is a first-class state and not a range preset. It colours the graph
 * from the latest bucket and keeps the shipped 30-second refetch, which is
 * load-bearing on the one page that is genuinely live-ops — a NOC wall map.
 * Collapsing it into "the last bucket of the selected range" silently kills
 * that liveness at 7d.
 *
 * `5m` is dropped here for the same reason it is dropped on the panels: at a
 * 120s poll cadence floored at 300s it is two to five points pretending to be a
 * trend.
 */
export type ViewsRange = 'live' | TrafficRange;

/** The spelling the throughput endpoint is asked for. */
export type ViewsQueryRange = 'now' | TrafficRange;

export const VIEWS_RANGES: ViewsRange[] = ['live', '1h', '6h', '24h', '7d'];

export function viewsRangeLabel(range: ViewsRange): string {
  return range === 'live' ? 'Live' : range;
}

/**
 * The window a panel opened under this page range should show.
 *
 * Live seeds an hour whose end tracks now, because a panel is a chart and a
 * chart of one bucket is a number.
 */
export function panelRange(range: ViewsRange): TrafficRange {
  return range === 'live' ? '1h' : range;
}

/**
 * What the topology throughput endpoint should be asked for.
 *
 * Live asks for the most recent bucket with no range reducer. Everything else
 * reduces the range with `max`, because a bucket that averages a microburst out
 * of existence produces a graph that contradicts the chart under it.
 */
export function graphQueryFor(range: ViewsRange): { timeRange: ViewsQueryRange; aggregation?: 'max' } {
  return range === 'live' ? { timeRange: 'now' } : { timeRange: range, aggregation: 'max' };
}
